import { TournamentMatchStatusEnum } from '../types/enums';
import {
  MATCHUP_PROGRESSION_ALL_MATCHES,
  MATCHUP_PROGRESSION_BEST_OF_N,
  MATCHUP_PROGRESSION_MANUAL,
} from './bracketCellMatchupSummary.util';
import type { BracketCellForm } from '../pages/tourDetails/TournamentPlay/components/PlayTag/PlayTag';

export const SECTION_PROGRESSION_MIXED = 'mixed';
export const SECTION_PROGRESSION_MANUAL = 'manual';

export type SectionProgressionMode =
  | typeof MATCHUP_PROGRESSION_ALL_MATCHES
  | typeof MATCHUP_PROGRESSION_BEST_OF_N
  | typeof SECTION_PROGRESSION_MIXED
  | typeof SECTION_PROGRESSION_MANUAL;

export type SectionProgressionMatch = {
  status?: number;
  results?: unknown[] | null;
  bracketCellProgressionMode?: number;
};

export type SectionProgressionCell = {
  cellId: string;
  matches: SectionProgressionMatch[];
};

export function implicitSectionProgressionMode(
  variant: 'group' | 'elimination',
): typeof MATCHUP_PROGRESSION_ALL_MATCHES | typeof MATCHUP_PROGRESSION_BEST_OF_N {
  return variant === 'group' ? MATCHUP_PROGRESSION_ALL_MATCHES : MATCHUP_PROGRESSION_BEST_OF_N;
}

export function getEffectiveCellProgressionMode(
  cellForm: Pick<BracketCellForm, 'progressionMode'> | undefined,
  primaryMatch: SectionProgressionMatch | undefined,
  implicitDefault: number,
): number {
  const formMode = cellForm?.progressionMode;
  if (formMode !== undefined && formMode !== '') {
    return Number(formMode);
  }
  if (typeof primaryMatch?.bracketCellProgressionMode === 'number') {
    return primaryMatch.bracketCellProgressionMode;
  }
  return implicitDefault;
}

export function inferSectionProgressionMode(
  cells: SectionProgressionCell[],
  bracketCells: Record<string, Pick<BracketCellForm, 'progressionMode'> | undefined>,
  implicitDefault: number,
): SectionProgressionMode {
  if (cells.length === 0) {
    return implicitDefault === MATCHUP_PROGRESSION_BEST_OF_N
      ? MATCHUP_PROGRESSION_BEST_OF_N
      : MATCHUP_PROGRESSION_ALL_MATCHES;
  }

  const modes = cells.map((cell) =>
    getEffectiveCellProgressionMode(bracketCells[cell.cellId], cell.matches[0], implicitDefault),
  );
  const first = modes[0];
  const uniform = modes.every((mode) => mode === first);
  if (uniform && first === MATCHUP_PROGRESSION_MANUAL) {
    return SECTION_PROGRESSION_MANUAL;
  }
  if (
    uniform &&
    (first === MATCHUP_PROGRESSION_ALL_MATCHES || first === MATCHUP_PROGRESSION_BEST_OF_N)
  ) {
    return first;
  }
  return SECTION_PROGRESSION_MIXED;
}

/** Play all and Best of N are copied onto a new matchup. Mixed and Manual keep the variant default. */
export function progressionModeForNewMatchup(
  inferred: SectionProgressionMode,
): typeof MATCHUP_PROGRESSION_ALL_MATCHES | typeof MATCHUP_PROGRESSION_BEST_OF_N | undefined {
  if (
    inferred === MATCHUP_PROGRESSION_ALL_MATCHES ||
    inferred === MATCHUP_PROGRESSION_BEST_OF_N
  ) {
    return inferred;
  }
  return undefined;
}

export function cellHasScoredMatch(matches: SectionProgressionMatch[]): boolean {
  return matches.some((match) => {
    if (
      match.status === TournamentMatchStatusEnum.FINISHED ||
      match.status === TournamentMatchStatusEnum.STARTED
    ) {
      return true;
    }
    return Array.isArray(match.results) && match.results.length > 0;
  });
}

export function countSectionProgressionChanges(
  cells: SectionProgressionCell[],
  bracketCells: Record<string, Pick<BracketCellForm, 'progressionMode'> | undefined>,
  nextMode: number,
  implicitDefault: number,
): { changeCount: number; scoredCount: number } {
  let changeCount = 0;
  let scoredCount = 0;
  for (const cell of cells) {
    const current = getEffectiveCellProgressionMode(
      bracketCells[cell.cellId],
      cell.matches[0],
      implicitDefault,
    );
    if (current !== nextMode) {
      changeCount += 1;
      if (cellHasScoredMatch(cell.matches)) {
        scoredCount += 1;
      }
    }
  }
  return { changeCount, scoredCount };
}

export function applySectionProgressionMode(
  setFieldValue: (field: string, value: unknown) => void,
  cellIds: string[],
  nextMode: number,
): void {
  for (const cellId of cellIds) {
    setFieldValue(`bracketCells.${cellId}.progressionMode`, nextMode);
  }
}
